import { Component, DoCheck, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { ModalController } from '@ionic/angular';
import { CookieService } from 'ngx-cookie-service';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';
import { DetailsPage as ProjectDetailsPage } from 'src/app/projects/details/details.page';
import { DetailsComponent as TeamMemberDetailsPage } from 'src/app/components/team-members/details/details.component';
import { DetailsPage as ClientDetailsPage } from 'src/app/clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from 'src/app/companies/details/details.page';
import { DetailsPage as TaskDetailsPage } from 'src/app/tasks/details/details.page';
import { DetailsPage as MissingInfoDetailsPage } from 'src/app/missing-info/details/details.page';
import { TodoEditComponent } from 'src/app/components/todos/edit/todo-edit.component';
import { CreateComponent as AppointmentCreateComponent } from 'src/app/components/team-members/calendar/appointments/create/create.component';
import { TodosService } from 'src/app/api/todos.service';
import { TimeAndBillingService } from 'src/app/time-and-billing/time-and-billing.service';

@Component({
  selector: 'app-time-and-billing-table ',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges, DoCheck {

  @Input() time_and_billing: any = null;
  @Input() projects: any = null;
  @Input() show_paging: any = null;
  @Input() show_expander = true;
  @Input() show_checkbox = false;
  @Input() hideColumns: string[] = [];
  @Input() hide_refresh_button = false;
  // The time tracking page has its own Show/Hide Columns button in its toolbar; everywhere else the table shows its own
  @Input() hide_column_picker = false;
  // Same for the Group View / Table View buttons and Group By: the Time Tracking page shows them in its own toolbar
  @Input() hide_grouping_controls = false;
  // On a project's or task's Time Entries tab: shows a button to add time right there, filled in with this project / task
  // (and its client)
  @Input() create_project: any = null;
  @Input() create_task: any = null;
  @Output() refresh = new EventEmitter<void>();
  // The page shows the current quick search in its description line
  @Output() quickSearch = new EventEmitter<string>();

  quickSearchTerm = '';
  selectedGuids = new Set<string>();
  bulkTimeStatus = 'WIP';
  team_members: any = null;
  billing_rates: any = null;
  max_per_page = 100;
  today: Date;
  tags: any[];
  matchModeOptions = [];
  singleMatchModeOptions = [
    { label: 'Includes Any', value: 'singleIncludesAny' },
    { label: 'Includes All', value: 'singleIncludesAll' },
  ];

  statusOptions = [
    { label: 'WIP - Initially Entered', value: 0 },
    { label: 'WIP - Waiting For Approval', value: 1 },
    { label: 'WIP - Ready For Invoice', value: 2 },
    { label: 'Cleared - Invoiced', value: 3 },
    { label: 'Cleared - Write Off', value: 4 },
  ];

  billableOptions = [
    { label: 'Billable', value: true },
    { label: 'Non-Billable', value: false },
  ];

  @ViewChild('dt') timeBillingTable: Table;
  activeTimeBillingFilterLabels: string[] = [];
  // Not private - the time tracking page binds this into app-filter-presets' [fieldLabelMap].
  readonly timeBillingFilterLabelMap: { [field: string]: string } = {
    title: 'Title',
    user_name: 'Team Member',
    status: 'Status',
    'linked.text': 'Linked',
    'client.display_name': 'Client',
    billable: 'Billable',
    start_date: 'Date',
    tags: 'Tags',
  };

  onTimeBillingTableFilter(event: any) {
    this.activeTimeBillingFilterLabels = this.computeActiveFilterLabels(event.filters, this.timeBillingFilterLabelMap);
  }

  clearTimeBillingFilters() {
    if (this.timeBillingTable) this.clearTableFilters(this.timeBillingTable);
    this.activeTimeBillingFilterLabels = [];
  }

  // For saved filters (the time tracking page's app-filter-presets): the table's column filters, and putting a saved set back
  GetColumnFilters(): any {
    return (this.timeBillingTable as any)?.filters ?? null;
  }

  ApplyColumnFilters(filters: any) {
    if (!this.timeBillingTable) return;
    const applied = filters ? JSON.parse(JSON.stringify(filters)) : {};
    (this.timeBillingTable as any).filters = applied;
    (this.timeBillingTable as any)._filter();
    this.activeTimeBillingFilterLabels = this.computeActiveFilterLabels(applied, this.timeBillingFilterLabelMap);
  }

  // ── Columns: show / hide and order (the column picker), remembered between visits ──
  availableColumns: ColumnConfig[] = [
    { key: 'details', label: 'Details', visible: true },
    { key: 'title', label: 'Title', visible: true },
    { key: 'start_date', label: 'Date', visible: true },
    { key: 'user_name', label: 'Team Member Assigned', visible: true },
    { key: 'linked', label: 'Linked', visible: true },
    { key: 'client', label: 'Client Assigned To', visible: true },
    { key: 'time_worked', label: 'Time Worked', visible: true },
    { key: 'exact_start_time', label: 'Exact Start Time', visible: true },
    { key: 'exact_end_time', label: 'Exact End Time', visible: true },
    { key: 'exact_total_time', label: 'Exact Total Time', visible: true },
    { key: 'rounded_start_time', label: 'Rounded Start Time', visible: true },
    { key: 'rounded_end_time', label: 'Rounded End Time', visible: true },
    { key: 'rounded_total_time', label: 'Rounded Total Time', visible: true },
    { key: 'billing_rate', label: 'Billing Rate', visible: true },
    { key: 'amount', label: 'Amount', visible: true },
    { key: 'time_status', label: 'Status', visible: true },
    { key: 'tags', label: 'Tags', visible: true },
  ];

  private static readonly COLUMNS_COOKIE = 'timeTableColumns';

  // A host that passes its own hideColumns (a project's time tab, say) decides what is shown there; only a table with
  // none of its own uses what was saved from the column picker
  private InitColumns() {
    if (!this.hideColumns?.length) {
      try {
        const saved = JSON.parse(this.cookieService.get(TableComponent.COLUMNS_COOKIE) || 'null');
        if (saved && Array.isArray(saved.hideColumns)) this.hideColumns = saved.hideColumns;
        if (saved && Array.isArray(saved.columnOrder)) this.ApplyColumnOrder(saved.columnOrder);
      } catch { /* an unreadable cookie just means the defaults */ }
    }
    // A host with its own compact layout (a project's time tab) doesn't get the exact / rounded time columns
    else this.hideColumns = [...this.hideColumns, ...TableComponent.EXACT_ROUNDED_COLUMNS.filter(key => !this.hideColumns.includes(key))];
    this.ApplyBillingColumns();
    this.syncColumnsFromHideColumns();
  }

  private static readonly EXACT_ROUNDED_COLUMNS = ['exact_start_time', 'exact_end_time', 'exact_total_time', 'rounded_start_time', 'rounded_end_time', 'rounded_total_time'];

  // Without the permission to view billing information there are no rate / amount columns at all - not in the table, not in
  // the totals, not even in the column picker. A team member's permissions can arrive after the table has been created, so
  // this is checked on every change detection (cheap) and the columns follow the permission.
  private billing_columns_shown: boolean | null = null;
  private readonly billing_column_defs: ColumnConfig[] = [
    { key: 'billing_rate', label: 'Billing Rate', visible: true },
    { key: 'amount', label: 'Amount', visible: true },
  ];

  ngDoCheck() {
    if (this.billing_columns_shown !== this.globalService.CanViewBillingInfo) {
      this.ApplyBillingColumns();
      this.syncColumnsFromHideColumns();
    }
  }

  private ApplyBillingColumns() {
    const allowed = this.globalService.CanViewBillingInfo;
    this.billing_columns_shown = allowed;
    const billing_keys = this.billing_column_defs.map(col => col.key);
    if (!allowed) {
      this.availableColumns = this.availableColumns.filter(col => !billing_keys.includes(col.key));
      return;
    }
    // (Re)added next to the time worked column when the permission turns out to be there after all
    const missing = this.billing_column_defs.filter(def => !this.availableColumns.some(col => col.key === def.key));
    if (missing.length) {
      const columns = [...this.availableColumns];
      const at = columns.findIndex(col => col.key === 'time_worked') + 1;
      columns.splice(at > 0 ? at : columns.length, 0, ...missing.map(def => ({ ...def })));
      this.availableColumns = columns;
    }
  }

  private syncColumnsFromHideColumns() {
    this.availableColumns.forEach(col => col.visible = !this.hideColumns.includes(col.key));
  }

  private syncHideColumnsFromColumns() {
    this.hideColumns = this.availableColumns.filter(col => !col.visible).map(col => col.key);
  }

  private ApplyColumnOrder(order: string[]) {
    const position = (key: string) => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
    this.availableColumns = [...this.availableColumns].sort((a, b) => position(a.key) - position(b.key));
  }

  async OpenColumnPicker() {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));
    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: { columns: columnsCopy },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        this.availableColumns = data.data.columns;
        this.syncHideColumnsFromColumns();
        this.cookieService.set(TableComponent.COLUMNS_COOKIE, JSON.stringify({
          hideColumns: this.hideColumns,
          columnOrder: this.availableColumns.map(col => col.key)
        }), 30, '/');
      }
    });
    return await modal.present();
  }

  private clearTableFilters(t: Table): void {
    if (!t) return;
    (t as any).tableService.onResetChange();
    if ((t as any).isStateful()) (t as any).clearState();
    (t as any)._filter();
  }

  private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
    if (!filters) return [];
    const active: string[] = [];
    for (const field of Object.keys(labelMap)) {
      const raw = filters[field];
      if (!raw) continue;
      const constraints = Array.isArray(raw) ? raw : [raw];
      const hasValue = constraints.some((c: any) => {
        if (c.value === null || c.value === undefined) return false;
        if (Array.isArray(c.value)) return c.value.length > 0;
        if (typeof c.value === 'string') return c.value.trim().length > 0;
        return true;
      });
      if (hasValue) active.push(labelMap[field]);
    }
    return active;
  }
  
  constructor(public globalService: GlobalService, private apiService: ApiService, private modalController: ModalController, private timeAndBillingService: TimeAndBillingService, private todosService: TodosService, private cookieService: CookieService) {
    this.matchModeOptions = [
      {
        label: 'Includes Any',
        value: "includesAny"
      },
      {
        label: 'Includes All',
        value: "includesAll"
      }
    ];
  }

  ngOnInit() {
    this.InitColumns();
    this.LoadGrouping();
    this.GetTags();
    if (this.time_and_billing?.length) this.LoadInfo();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.time_and_billing && this.time_and_billing?.length) {
      this.LoadInfo();
    }
  }

  async LoadInfo() {
    if (!this.time_and_billing?.length) return;

    if (!this.team_members) {
      const tm_data: any = await this.apiService.get('team_members');
      this.team_members = tm_data?.data || [];
    }
    if (!this.billing_rates) {
      const bi_data: any = await this.apiService.get('billing/rates');
      this.billing_rates = bi_data?.data || [];
    }

    this.time_and_billing.forEach(time => {
      if (time.start_date && !(time.start_date instanceof Date)) time.start_date = new Date(time.start_date);
      if (time.end_date && !(time.end_date instanceof Date)) time.end_date = new Date(time.end_date);
      if (!time.user_name) {
        if (time.user_guid === '0000') {
          time.user_name = 'Administrator';
        } else {
          const idx = this.globalService.GetArrayIndex(this.team_members, time.user_guid);
          time.user_name = idx !== -1 ? this.team_members[idx].display_name : '-';
        }
      }
      if (!time.billing_rate) {
        const idx = this.globalService.GetArrayIndex(this.billing_rates, time.billing_rate_guid);
        time.billing_rate = idx !== -1
          ? this.globalService.MakeJSONCopy(this.billing_rates[idx])
          : { code: '-', rate: 0 };
        if (time.cust_rate) time.billing_rate.rate = time.cust_rate;
      }
    });
  }

  async presentDetailsModal(entry) {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal secondary-modal',
      componentProps: { entry },
      backdropDismiss: true
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.deleted) this.Refresh();
    });
    return await modal.present();
  }

  async ViewProject(entry, event: Event) {
    event.stopPropagation();
    if (!entry?.project?.guid) return;
    const modal = await this.modalController.create({
      component: ProjectDetailsPage,
      cssClass: 'large-modal secondary-modal',
      componentProps: { guid: entry.project.guid },
      backdropDismiss: true
    });
    return await modal.present();
  }

  async ViewTeamMember(entry, event: Event) {
    event.stopPropagation();
    if (!entry?.user_guid) return;
    const modal = await this.modalController.create({
      component: TeamMemberDetailsPage,
      cssClass: 'large-modal',
      componentProps: { team_member: { guid: entry.user_guid } },
      backdropDismiss: false
    });
    return await modal.present();
  }

  async ViewClient(entry, event: Event) {
    event.stopPropagation();
    const guid = entry?.client?.guid || entry?.client_guid;
    if (!guid) return;
    const modal = await this.modalController.create({
      component: ClientDetailsPage,
      cssClass: 'large-modal',
      componentProps: { client: { guid } },
      backdropDismiss: false
    });
    return await modal.present();
  }

  async ViewCompany(entry, event: Event) {
    event.stopPropagation();
    const guid = entry?.company?.guid || entry?.company_guid;
    if (!guid) return;
    const modal = await this.modalController.create({
      component: CompanyDetailsPage,
      cssClass: 'large-modal',
      componentProps: { company: { guid } },
      backdropDismiss: false
    });
    return await modal.present();
  }

  // Opens the details of whatever the entry is linked to (the Linked column)
  async ViewLinked(entry, event: Event) {
    event.stopPropagation();
    const linked = entry?.linked;
    if (!linked?.guid) return;
    switch (linked.type) {
      case 'project':
        return await this.ViewProject(entry, event);
      case 'task':
        return await this.OpenLinkedModal(TaskDetailsPage, { guid: linked.guid }, 'large-modal');
      case 'missing_info':
        return await this.OpenLinkedModal(MissingInfoDetailsPage, { guid: linked.guid }, 'large-modal');
      case 'todo': {
        const data: any = await this.todosService.GetTodo(linked.guid);
        const todo = data?.success ? data.data?.[0] : null;
        if (!todo) {
          this.globalService.presentToastMessage(data?.message || 'This To Do could not be found - it may have been deleted.', false);
          return;
        }
        return await this.OpenLinkedModal(TodoEditComponent, { todo }, 'large-modal');
      }
      case 'appointment': {
        const data: any = await this.apiService.get(`calendar/events/${linked.guid}`);
        const appointment = data?.success ? data.data?.[0] : null;
        if (!appointment) {
          this.globalService.presentToastMessage(data?.message || 'This appointment could not be found - it may have been cancelled.', false);
          return;
        }
        const team_members = (appointment.users || []).filter((u: any) => u.type == 1);
        return await this.OpenLinkedModal(AppointmentCreateComponent, {
          view_only: true,
          event_guid: appointment.guid,
          force_type: appointment.type,
          title: appointment.title,
          description: appointment.description,
          start_time: appointment.start_time,
          in_end_time: appointment.end_time,
          in_users: team_members,
          in_selected_users: team_members.map((u: any) => u.guid),
          in_assigned_clients: this.globalService.ResolveAssignedClients(appointment)
        }, 'tall-modal secondary-modal');
      }
    }
  }

  private async OpenLinkedModal(component: any, componentProps: any, cssClass: string) {
    const modal = await this.modalController.create({ component, cssClass, componentProps, backdropDismiss: false });
    return await modal.present();
  }

  async GetTags() {
    let data: any = await this.apiService.GetTags('time_and_billing');
    this.tags = data.data;
  }

  // What is typed in the box (shown immediately); quickSearchTerm is what the table is actually filtered by, which
  // follows it ~250ms after typing stops so the table isn't re-filtered on every keystroke
  quickSearchInput = '';
  private quickSearchTimer: any = null;

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term ?? '';
    if (this.quickSearchTimer) clearTimeout(this.quickSearchTimer);
    const apply = () => {
      this.quickSearchTimer = null;
      this.quickSearchTerm = this.quickSearchInput;
      this.quickSearch.emit(this.quickSearchTerm);
      if (this.timeBillingTable) this.timeBillingTable.filterGlobal(this.quickSearchTerm, 'contains');
    };
    // Clearing applies straight away, typing waits for a pause
    if (!this.quickSearchInput.trim()) apply();
    else this.quickSearchTimer = setTimeout(apply, 250);
  }

  Refresh() {
    this.refresh.emit();
  }

  get canCreateHere(): boolean {
    return !!(this.create_project || this.create_task);
  }

  // Opens a new time entry for the project / task this table is showing. The entry reloads the lists itself when it is saved.
  async OpenCreateModal() {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { project: this.create_project || null, task: this.create_task || null },
      backdropDismiss: false
    });
    return await modal.present();
  }

  get allSelected(): boolean {
    return this.visibleEntries.length > 0 && this.visibleEntries.every(e => this.selectedGuids.has(e.guid));
  }

  get someSelected(): boolean {
    return this.visibleEntries.some(e => this.selectedGuids.has(e.guid));
  }

  toggleSelectEntry(guid: string) {
    if (this.selectedGuids.has(guid)) {
      this.selectedGuids.delete(guid);
    } else {
      this.selectedGuids.add(guid);
    }
    this.selectedGuids = new Set(this.selectedGuids);
  }

  toggleSelectAll() {
    if (this.allSelected) {
      this.selectedGuids.clear();
    } else {
      this.visibleEntries.forEach(e => this.selectedGuids.add(e.guid));
    }
    this.selectedGuids = new Set(this.selectedGuids);
  }

  clearSelection() {
    this.selectedGuids = new Set();
  }

  applyBulkTimeStatus() {
    const guids = Array.from(this.selectedGuids);
    this.timeAndBillingService.SetTimeStatus(guids, this.bulkTimeStatus).then((data: any) => {
      if (!data?.success) {
        this.globalService.presentToastMessage(data?.message, false);
        return;
      }
      guids.forEach(guid => {
        const entry = (this.time_and_billing || []).find((e: any) => e.guid === guid);
        if (entry) entry.time_status = this.bulkTimeStatus;
      });
    });
    this.selectedGuids = new Set();
  }

  async updateTimeStatus(entry: any, status: string) {
    const previous = entry.time_status;
    entry.time_status = status;
    const data: any = await this.timeAndBillingService.SetTimeStatus([entry.guid], status);
    if (!data?.success) {
      entry.time_status = previous;
      this.globalService.presentToastMessage(data?.message, false);
    }
  }

  get visibleEntries(): any[] {
    return this.timeBillingTable?.filteredValue ?? this.time_and_billing ?? [];
  }

  private FormatMinutes(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  }

  get totalTimeFormatted(): string {
    return this.FormatMinutes(this.visibleEntries.reduce((sum, e) => sum + (e.hours || 0) * 60 + (e.minutes || 0), 0));
  }

  // The time for the visible entries with this time status (an entry with no status is WIP)
  private TimeForStatus(statuses: string[]): string {
    return this.FormatMinutes(this.visibleEntries
      .filter(e => statuses.includes(e.time_status || 'WIP'))
      .reduce((sum, e) => sum + (e.hours || 0) * 60 + (e.minutes || 0), 0));
  }

  get timeWip(): string { return this.TimeForStatus(['WIP']); }
  get timeClearedInvoiced(): string { return this.TimeForStatus(['Cleared - Invoiced']); }
  get timeClearedWriteOff(): string { return this.TimeForStatus(['Cleared - Write Off']); }
  get timeCleared(): string { return this.TimeForStatus(['Cleared - Invoiced', 'Cleared - Write Off']); }

  // Billed on the rounded time when the entry's billing rate rounds, otherwise on the exact time
  EntryAmount(e: any): number {
    if (!(e.billing_rate_per_hour > 0)) return 0;
    const minutes = e.rounded ? (e.rounded_hours || 0) * 60 + (e.rounded_minutes || 0) : (e.hours || 0) * 60 + (e.minutes || 0);
    return (minutes / 60) * e.billing_rate_per_hour;
  }

  get totalAmount(): string {
    return '$' + this.visibleEntries.reduce((sum, e) => sum + this.EntryAmount(e), 0).toFixed(2);
  }

  // The amount for the visible entries with this time status (an entry with no status is WIP)
  private AmountForStatus(statuses: string[]): string {
    const total = this.visibleEntries
      .filter(e => statuses.includes(e.time_status || 'WIP'))
      .reduce((sum, e) => sum + this.EntryAmount(e), 0);
    return '$' + total.toFixed(2);
  }

  get totalWip(): string { return this.AmountForStatus(['WIP']); }
  get totalClearedInvoiced(): string { return this.AmountForStatus(['Cleared - Invoiced']); }
  get totalClearedWriteOff(): string { return this.AmountForStatus(['Cleared - Write Off']); }
  get totalCleared(): string { return this.AmountForStatus(['Cleared - Invoiced', 'Cleared - Write Off']); }

  // ── Exact and rounded time ──
  // The exact time is what staff entered; the rounded set only exists when the entry's billing rate rounds
  ExactMinutes(e: any): number {
    return (e.hours || 0) * 60 + (e.minutes || 0);
  }

  RoundedMinutes(e: any): number {
    return e.rounded ? (e.rounded_hours || 0) * 60 + (e.rounded_minutes || 0) : 0;
  }

  // "47m" / "1h 5m"
  FormatDuration(totalMinutes: number): string {
    return this.FormatMinutes(Math.round(totalMinutes));
  }

  get totalExactTime(): string {
    return this.FormatDuration(this.visibleEntries.reduce((sum, e) => sum + this.ExactMinutes(e), 0));
  }

  // The rounded time where the entry rounds, otherwise its exact time
  get totalRoundedTime(): string {
    return this.FormatDuration(this.visibleEntries.reduce((sum, e) => sum + (e.rounded ? this.RoundedMinutes(e) : this.ExactMinutes(e)), 0));
  }

  // ── Grouping ──
  readonly groupByOptions = [
    { value: 'client', label: 'Clients' },
    { value: 'team_member', label: 'Team Members' },
    { value: 'group', label: 'Groups' },
    { value: 'linked_type', label: 'Projects, To Dos, Appointments, Misc' },
    { value: 'client/group', label: 'Clients / Groups' },
    { value: 'client/project', label: 'Clients / Projects' },
    { value: 'client/group/project', label: 'Clients / Groups / Projects' },
    { value: 'team_member/group', label: 'Team Members / Groups' },
    { value: 'team_member/project', label: 'Team Members / Projects' },
    { value: 'team_member/group/project', label: 'Team Members / Groups / Projects' },
  ];
  // Like the projects page: Group View (grouped by what is chosen) or Table View (the plain table). Remembered between visits.
  groupView = false;
  group_by = 'client';
  private collapsedGroups = new Set<string>();
  private groupCache: { entries: any[], group_by: string, version: number, tree: any[] } | null = null;
  private groupVersion = 0;
  private readonly noGroups: any[] = [];
  private static readonly GROUPING_COOKIE = 'timeTableGrouping';

  private LoadGrouping() {
    try {
      const saved = JSON.parse(this.cookieService.get(TableComponent.GROUPING_COOKIE) || 'null');
      if (saved && typeof saved.groupView === 'boolean') this.groupView = saved.groupView;
      if (saved && this.groupByOptions.some(option => option.value === saved.group_by)) this.group_by = saved.group_by;
    } catch { /* an unreadable cookie just means the defaults */ }
  }

  private SaveGrouping() {
    this.cookieService.set(TableComponent.GROUPING_COOKIE, JSON.stringify({ groupView: this.groupView, group_by: this.group_by }), 30, '/');
  }

  get isGrouped(): boolean {
    return this.groupView;
  }

  SetGroupView(group_view: boolean) {
    this.groupView = group_view;
    this.SaveGrouping();
  }

  OnGroupByChange(value: string) {
    this.group_by = value;
    this.collapsedGroups.clear();
    this.groupVersion++;
    this.SaveGrouping();
  }

  // The visible columns, in order (what the grouped view's header and rows are built from)
  get shownColumns(): ColumnConfig[] {
    return this.availableColumns.filter(col => !this.hideColumns.includes(col.key));
  }

  ToggleGroup(node: any) {
    if (this.collapsedGroups.has(node.key)) this.collapsedGroups.delete(node.key);
    else this.collapsedGroups.add(node.key);
  }

  IsCollapsed(node: any): boolean {
    return this.collapsedGroups.has(node.key);
  }

  // What an entry falls under for one grouping level - usually one thing, but an entry whose project has several groups
  // is counted under each of them
  private ResolveLevel(entry: any, level: string): { key: string, label: string, colour?: string }[] {
    switch (level) {
      case 'client':
        if (entry.client) return [{ key: 'c:' + entry.client.guid, label: entry.client.display_name || 'Unnamed contact' }];
        if (entry.company) return [{ key: 'c:' + entry.company.guid, label: entry.company.name || entry.company.display_name || 'Unnamed company' }];
        return [{ key: 'none', label: 'No Client' }];
      case 'team_member':
        return entry.user_guid ? [{ key: 'u:' + entry.user_guid, label: entry.user_name || '-' }] : [{ key: 'none', label: 'No Team Member' }];
      case 'group': {
        const groups: any[] = Array.isArray(entry.project_groups) ? entry.project_groups.filter((g: any) => g?.guid) : [];
        return groups.length ? groups.map((g: any) => ({ key: 'g:' + g.guid, label: g.title || 'Unnamed group', colour: g.colour })) : [{ key: 'none', label: 'No Group', colour: '#adb5bd' }];
      }
      case 'project':
        return entry.project_guid ? [{ key: 'p:' + entry.project_guid, label: entry.project_title || entry.project?.title || 'Unnamed project' }] : [{ key: 'none', label: 'No Project' }];
      case 'linked_type': {
        const type = entry.linked?.type;
        if (type === 'project' || type === 'task') return [{ key: 'projects', label: 'Projects' }];
        if (type === 'todo') return [{ key: 'todos', label: 'To Dos' }];
        if (type === 'appointment') return [{ key: 'appointments', label: 'Appointments' }];
        return [{ key: 'none', label: 'Misc' }];
      }
    }
    return [{ key: 'none', label: '-' }];
  }

  private SumEntries(entries: any[]) {
    let worked = 0, exact = 0, rounded = 0, amount = 0;
    entries.forEach(e => {
      worked += e.start_date && e.end_date ? Math.max(0, Math.round((new Date(e.end_date).getTime() - new Date(e.start_date).getTime()) / 60000)) : 0;
      exact += this.ExactMinutes(e);
      rounded += e.rounded ? this.RoundedMinutes(e) : this.ExactMinutes(e);
      amount += this.EntryAmount(e);
    });
    return { count: entries.length, worked, exact, rounded, amount };
  }

  // A group is a node with its entries, its totals and (when there are more levels) its sub groups
  private BuildGroupTree(entries: any[], levels: string[], depth: number, parentKey: string): any[] {
    const level = levels[depth];
    const buckets = new Map<string, { label: string, colour: string, entries: any[] }>();
    entries.forEach(entry => {
      this.ResolveLevel(entry, level).forEach(({ key, label, colour }) => {
        if (!buckets.has(key)) buckets.set(key, { label, colour: colour || '#e9ecef', entries: [] });
        buckets.get(key).entries.push(entry);
      });
    });
    // Alphabetical, with the "No ..." bucket last
    const ordered = Array.from(buckets.entries()).sort((a, b) =>
      (a[0] === 'none' ? 1 : 0) - (b[0] === 'none' ? 1 : 0) || a[1].label.localeCompare(b[1].label));
    return ordered.map(([key, bucket]) => {
      const node: any = { key: parentKey + '/' + key, label: bucket.label, colour: bucket.colour, depth, totals: this.SumEntries(bucket.entries), children: [], entries: [] };
      if (depth + 1 < levels.length) node.children = this.BuildGroupTree(bucket.entries, levels, depth + 1, node.key);
      else node.entries = [...bucket.entries].sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
      return node;
    });
  }

  // The groups for Group View, with the totals for each. Cached until the entries or the grouping changes.
  get groupTree(): any[] {
    const entries = this.visibleEntries;
    if (!this.groupView || !entries.length) return this.noGroups;
    const cache = this.groupCache;
    if (cache && cache.entries === entries && cache.group_by === this.group_by && cache.version === this.groupVersion) return cache.tree;
    const tree = this.BuildGroupTree(entries, this.group_by.split('/'), 0, '');
    this.groupCache = { entries, group_by: this.group_by, version: this.groupVersion, tree };
    return tree;
  }

}
